/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { PALETTE } from "styles/base/variables";

interface BaseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "text" | "outlined" | "contained";
  color?: string;
  css?: any;
  children: React.ReactNode;
}

function BaseButton({
  variant: _variant = "text",
  color,
  children,
  css: customCss,
  ...props
}: BaseButtonProps) {
  if (props.disabled) {
    color = PALETTE.textDisabled;
  }

  const cssRenderer = (variant: string) => {
    const defaultCss = css`
      padding: 11px 17px;
      border-radius: 4px;
      font-size: 16px;
      transition: background 0.3s;
    `;

    const textCss = css`
      ${defaultCss}
      background-color: transparent;
      border: none;
      color: ${color || PALETTE.text};
      ${!props.disabled &&
      `&:hover {
        opacity: 0.8;
        background: ${PALETTE.background};
      }`}
    `;

    const outlinedCss = css`
      ${defaultCss}
      background: ${PALETTE.light};
      border: 1px solid ${color || PALETTE.border};
      color: ${color || PALETTE.text};
      ${!props.disabled &&
      `&:hover {
        opacity: 0.8;
      }`}
    `;

    const containedCss = css`
      ${defaultCss}
      background: ${color};
      border: 1px solid ${color};
      color: ${PALETTE.light};
      ${!props.disabled &&
      `&:hover {
        opacity: 0.9;
      }`}
    `;

    switch (variant) {
      case "text":
        return textCss;
      case "outlined":
        return outlinedCss;
      case "contained":
        return containedCss;
      default:
        return textCss;
    }
  };

  return (
    <button
      {...props}
      type={props.type || "button"}
      css={[cssRenderer(_variant), customCss]}
    >
      {children}
    </button>
  );
}

export default BaseButton;
